/**
 * A client-generated id for each write this session makes (#800).
 *
 * The realtime event that announces a change is projected from an audit row, so
 * it can say *that* something changed and *that* this user changed it — never
 * *which write* it was. That missing fact is what made echo suppression a guess:
 * from inside `app/selfRefresh.ts`, "the sealed cycle's own echo" (must be
 * dropped, or every mutation refreshes twice) and "an uncovered write's echo"
 * (must refresh, or the write never renders) are the same observation.
 *
 * The id is stamped on the request, recorded on the audit row, echoed back on
 * the event, and matched here. It is opaque to the server: never read for
 * authorization, never trusted for identity, only handed back.
 *
 * The api client cannot import the suppression module directly — `lib` sits
 * under `app`, and the marks belong to a mounted App instance — so the app
 * subscribes and the client publishes through this seam.
 */

export function newWriteId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Fall through to the non-crypto id below.
  }
  return `write-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

type WriteListener = (writeId: string) => void;

let listener: WriteListener | null = null;

/**
 * Watch the writes this session issues. One listener: the App owns the
 * suppression ledger, and a second subscriber would mean a second ledger
 * disagreeing with it. Returns an unsubscribe for StrictMode's double-mount.
 */
export function onWriteIssued(next: WriteListener): () => void {
  listener = next;
  return () => {
    if (listener === next) listener = null;
  };
}

/**
 * Announce a write as it goes out — before the response, because the echo can
 * be delivered while the request is still in flight.
 */
export function noteWriteSent(writeId: string): void {
  if (!listener || !writeId) return;
  try {
    listener(writeId);
  } catch {
    // Bookkeeping must never fail a write. The cost of a lost note is one
    // refresh that was not strictly needed.
  }
}
